import { useEffect, useId, useRef } from 'react';

import { SCOPE_NOTES, type ScopeGroup } from '@/lib/apiKeysApi';

/**
 * Grouped admin-scope checklist with tri-state "All scopes" and per-group
 * checkboxes. Used by the API key mint + edit forms and the new-admin form.
 *
 * Every scope is always listed, so an operator can see what exists even
 * when they can't grant it:
 *
 *   • grantable        — enabled; the only scopes group/all toggles touch.
 *   • not grantable    — disabled and muted, with the reason.
 *   • stale            — held by the thing being edited but no longer by
 *                        the operator. Shown checked + disabled, and never
 *                        part of `selected`, so saving drops it.
 */
export function ScopePicker({
  groups,
  grantable,
  selected,
  onChange,
  stale = [],
  disabled = false,
}: {
  groups: readonly ScopeGroup[];
  grantable: readonly string[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
  stale?: readonly string[];
  disabled?: boolean;
}) {
  const baseId = useId();
  const canGrant = (s: string) => !disabled && grantable.includes(s);
  const allGrantable = groups.flatMap((g) => g.scopes).filter(canGrant);

  const setMany = (scopes: readonly string[], on: boolean) => {
    const next = on
      ? [...selected, ...scopes.filter((s) => !selected.includes(s))]
      : selected.filter((s) => !scopes.includes(s));
    // Keep display order stable regardless of click order.
    const order = groups.flatMap((g) => g.scopes);
    onChange(next.sort((a, b) => order.indexOf(a) - order.indexOf(b)));
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <TriStateRow
        id={`${baseId}-all`}
        label="All scopes"
        strong
        scopes={allGrantable}
        selected={selected}
        onToggle={setMany}
      />
      {groups.map((g) => {
        const groupGrantable = g.scopes.filter(canGrant);
        return (
          <fieldset key={g.id} style={fieldset}>
            <legend style={{ padding: 0 }}>
              <TriStateRow
                id={`${baseId}-group-${g.id}`}
                label={g.label}
                title={g.description}
                scopes={groupGrantable}
                selected={selected}
                onToggle={setMany}
              />
            </legend>
            <div style={{ display: 'grid', gap: 8, paddingLeft: 26, marginTop: 8 }}>
              {g.scopes.map((s) => {
                const id = `${baseId}-scope-${s}`;
                const isStale = stale.includes(s);
                const enabled = canGrant(s) && !isStale;
                const reason = isStale
                  ? "No longer active: you don't hold this scope. It's removed when you save."
                  : !grantable.includes(s)
                    ? "Your account doesn't have this scope"
                    : null;
                return (
                  <div
                    key={s}
                    style={{ ...scopeRow, opacity: enabled || isStale ? 1 : 0.55 }}
                    title={reason ?? undefined}
                  >
                    <input
                      id={id}
                      type="checkbox"
                      checked={isStale || selected.includes(s)}
                      disabled={!enabled}
                      aria-describedby={`${id}-note`}
                      onChange={(e) => setMany([s], e.target.checked)}
                      style={checkbox}
                    />
                    <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                      <label htmlFor={id} style={{ cursor: enabled ? 'pointer' : 'default' }}>
                        <code style={{ fontSize: 12.5, fontFamily: 'var(--font-mono)' }}>{s}</code>
                      </label>
                      <div id={`${id}-note`} style={note}>
                        {SCOPE_NOTES[s] ?? ''}
                        {reason && (
                          <span style={{ display: 'block', color: isStale ? 'var(--coral-ink)' : 'var(--ink-3)', fontStyle: 'italic' }}>
                            {reason}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}

/**
 * Checkbox that reflects a set: checked when every scope in it is
 * selected, indeterminate when some are. Disabled when the set is empty
 * (nothing in it the operator can grant).
 */
function TriStateRow({
  id,
  label,
  title,
  strong = false,
  scopes,
  selected,
  onToggle,
}: {
  id: string;
  label: string;
  title?: string;
  strong?: boolean;
  scopes: readonly string[];
  selected: readonly string[];
  onToggle: (scopes: readonly string[], on: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const count = scopes.filter((s) => selected.includes(s)).length;
  const all = scopes.length > 0 && count === scopes.length;
  const some = count > 0 && !all;
  const empty = scopes.length === 0;

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = some;
  }, [some]);

  return (
    <div style={{ ...scopeRow, alignItems: 'center', opacity: empty ? 0.55 : 1 }} title={title}>
      <input
        ref={ref}
        id={id}
        type="checkbox"
        checked={all}
        disabled={empty}
        onChange={() => onToggle(scopes, !all)}
        style={checkbox}
      />
      <label
        htmlFor={id}
        style={{
          fontSize: 14,
          fontWeight: strong ? 600 : 500,
          color: 'var(--ink)',
          cursor: empty ? 'default' : 'pointer',
        }}
      >
        {label}
      </label>
    </div>
  );
}

const fieldset: React.CSSProperties = {
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-md)',
  padding: '10px 14px 12px',
  margin: 0,
  minWidth: 0,
};
const scopeRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
};
const checkbox: React.CSSProperties = {
  marginTop: 2,
  width: 16,
  height: 16,
  accentColor: 'var(--coral)',
  flexShrink: 0,
};
const note: React.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--ink-3)',
  lineHeight: 1.4,
  marginTop: 2,
};
